import { useCallback, useEffect, useRef, useState } from "react";
import { getDataAdapter } from "@/data";
import { emitDataEvent } from "@/data/events";
import { useAuth } from "@/auth/AuthProvider";
import { durationUnit, roundDurationMinutes } from "@/utils/duration";
const KEY = "ttp.activeTimer";
function load() {
    try {
        const raw = localStorage.getItem(KEY);
        return raw ? JSON.parse(raw) : null;
    }
    catch {
        return null;
    }
}
export function useTimer() {
    const { user } = useAuth();
    const adapter = getDataAdapter();
    const [active, setActive] = useState(() => load());
    const [tick, setTick] = useState(0);
    const intervalRef = useRef(null);
    useEffect(() => {
        if (!active) {
            if (intervalRef.current) {
                window.clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            return;
        }
        intervalRef.current = window.setInterval(() => setTick((t) => t + 1), 1000);
        return () => {
            if (intervalRef.current)
                window.clearInterval(intervalRef.current);
        };
    }, [active]);
    const start = useCallback((t) => {
        localStorage.setItem(KEY, JSON.stringify(t));
        setActive(t);
    }, []);
    const stop = useCallback(async () => {
        if (!active)
            return;
        const end = new Date();
        const start = new Date(active.startedAt);
        const rawMinutes = (end.getTime() - start.getTime()) / 60000;
        const categories = active.transactionCategoryId ? await adapter.listTransactionCategories() : [];
        const minutes = roundDurationMinutes(rawMinutes, durationUnit(active.transactionCategoryId, categories));
        const roundedEnd = new Date(start.getTime() + minutes * 60000);
        await adapter.createTimeEntry({
            msdyn_description: active.description,
            msdyn_start: active.startedAt,
            msdyn_end: roundedEnd.toISOString(),
            msdyn_duration: minutes,
            msdyn_type: active.type,
            msdyn_entryStatus: "Draft",
            bookableresource: user.bookableResourceId,
            regardingobjectid: active.regarding,
            msdyn_transactioncategoryid: active.transactionCategoryId,
            resourceCategoryId: active.resourceCategoryId,
            msdyn_projecttaskid: active.projectTaskId,
            msdyn_bookableresourcebooking: active.bookingId,
            crb7a_detailsofwork: active.internalDescription,
            msdyn_externaldescription: active.externalComments,
        });
        localStorage.removeItem(KEY);
        setActive(null);
        setTick((t) => t + 1);
        // Notify pages (Entries, Dashboard) that a new entry just landed.
        // App-level reloadKey doesn't reach sibling pages — this does.
        emitDataEvent("entries-changed");
    }, [active, adapter, user.bookableResourceId]);
    return { active, start, stop, tick };
}
